import { useRollbar } from '@rollbar/react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useActions } from 'common/hooks/useActions'
import {
  deleteUserBlock,
  deleteCommonBlock,
  deleteMasterBlock,
} from 'client/actions/blockActions'
import { createBlockAtTheBottom } from 'client/actions/entityActions'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import useBlocks, { selectors as blocksSelectors } from 'client/hooks/useBlocks'
import useManagement from 'client/hooks/useManagement'
import usePresentPage, {
  selectors as pageSelectors,
} from 'client/hooks/usePresentPage'
import { isUserAdmin } from 'client/reducers/managementReducer'
import { Block } from './block'
import { blockCategories } from './consts'
import { BlockCategoryType, BlockType } from './types'
import { BackButton } from 'src/editor/shared/components/back-button'

interface BlocksListingProps {
  activeCategory: BlockCategoryType | null
  setActiveCategory: React.Dispatch<
    React.SetStateAction<BlockCategoryType | null>
  >
}

export const BlocksListing = ({
  activeCategory,
  setActiveCategory,
}: BlocksListingProps) => {
  const { t } = useTranslation()
  const { isMobile } = useDeviceModeContext()
  const rollbar = useRollbar()
  const isTemplate = usePresentPage(pageSelectors.isTemplate)

  const commonBlocksByCategories: Record<BlockCategoryType, BlockType[]> =
    useBlocks(blocksSelectors.getCommonBlocksByCategories)
  const userBlocks: BlockType[] = useBlocks(blocksSelectors.getUserBlocks)
  const masterBlocks: BlockType[] = useBlocks(blocksSelectors.getMasterBlocks)
  const usedMasterBlockIds = usePresentPage(pageSelectors.getUsedMasterBlockIds)

  const deleteUserBlockAction = useActions(deleteUserBlock)
  const deleteMasterBlockAction = useActions(deleteMasterBlock)
  const addToTheBottom = useActions(createBlockAtTheBottom)
  const removeCommonBlock = useActions(deleteCommonBlock)

  const [searchValue, setSearchValue] = useState('')
  const isAdmin = useManagement(isUserAdmin)

  const isMasterBlockInUse = (block: BlockType) =>
    block.isMaster && usedMasterBlockIds.includes(block.id)

  function filterBySearchValue(searchValue: string) {
    return (filterable: { title: string }) => {
      return !searchValue.length || filterable.title.indexOf(searchValue) > -1
    }
  }

  const userBlocksFilteredList = userBlocks.filter(
    filterBySearchValue(searchValue),
  )

  const masterBlockFilteredList = masterBlocks.filter(
    filterBySearchValue(searchValue),
  )

  return (
    <>
      <BackButton className="mb-5!" onClick={() => setActiveCategory(null)} />
      {activeCategory === blockCategories.userSimple && (
        <>
          <input
            type="search"
            placeholder={t('components.core.blocks.search.placeholder')}
            onChange={e => setSearchValue(e.target.value)}
            className="mt-1 mb-0.5 h-9 w-full rounded-lg border border-inputBorder border-solid p-1 indent-1 text-S shadow-[inset_1px_1px_1px_rgba(130, 137, 150, 0.23)] [&::-webkit-search-cancel-button]:cursor-pointer"
          />
          <div className="max-h-[calc(100vh - 200px)] overflow-auto overflow-x-hidden p-0">
            {userBlocksFilteredList.map(block => {
              return (
                <Block
                  key={block.id}
                  block={block}
                  remove={() => deleteUserBlockAction(block.id, rollbar)}
                  addToTheBottom={addToTheBottom}
                  isRemoveAllowed
                  isSharingAllowed
                  isMobile={isMobile}
                />
              )
            })}
          </div>
        </>
      )}
      {!isTemplate && activeCategory === blockCategories.userMaster && (
        <>
          <input
            type="search"
            placeholder={t('components.core.blocks.search.placeholder')}
            onChange={e => setSearchValue(e.target.value)}
            className="mt-1 mb-0.5 h-9 w-full rounded-md border border-inputBorder border-solid p-1 indent-1 text-S shadow-[inset_1px_1px_1px_rgba(130, 137, 150, 0.23)] [&::-webkit-search-cancel-button]:cursor-pointer"
          />
          <div className="max-h-[calc(100vh - 200px)] overflow-auto overflow-x-hidden p-0">
            {masterBlockFilteredList.map(block => {
              return (
                <Block
                  key={block.id}
                  block={block}
                  remove={() =>
                    block.isMaster
                      ? deleteMasterBlockAction(block.id)
                      : deleteUserBlockAction(block.id, rollbar)
                  }
                  isMasterBlockInUse={isMasterBlockInUse(block)}
                  addToTheBottom={addToTheBottom}
                  isRemoveAllowed
                  isSharingAllowed
                  isMobile={isMobile}
                />
              )
            })}
          </div>
        </>
      )}
      {activeCategory && activeCategory !== blockCategories.user && (
        <div className="max-h-[calc(100vh - 200px)] overflow-auto overflow-x-hidden p-0">
          {commonBlocksByCategories[activeCategory]?.map(block => (
            <Block
              key={block.id}
              block={block}
              remove={() => removeCommonBlock(block.id, rollbar)}
              addToTheBottom={addToTheBottom}
              isRemoveAllowed={isAdmin}
              isMobile={isMobile}
            />
          ))}
        </div>
      )}
    </>
  )
}
